import type { Emitter } from "@core/events/emitter";

export type DocLibrarySource =
  | { readonly kind: "product"; readonly productKey: string }
  | { readonly kind: "workflow"; readonly workflowId: string; readonly knowledgeKey: string };

/**
 * One row of the library's **Docs** tab, sourced from the loaded workflow's
 * product document set and/or direct `knowledge.include` references.
 */
export interface DocLibraryItem {
  /** Stable document identity in the product document store. */
  readonly storagePath: string;
  /** Canonical document-name key (`!document:{key}` without the prefix). */
  readonly documentNameKey: string;
  /** The UPLOADED file's name. IDENTITY, not a label — see `displayName`. */
  readonly fileName: string;
  /** Upper-cased extension (`"PDF"`). */
  readonly format: string;
  readonly fileSize: number;
  /** Uploader / source label. */
  readonly userTag: string;
  /** Parsed `LastModifiedUtc` (epoch ms; 0 when unparseable → sorts last). */
  readonly lastModifiedMs: number;
  /**
   * What a PERSON should see — the Define app's `name`. Empty when the row
   * states none; render `displayName || fileName`.
   *
   * ⚠ `fileName` is IDENTITY (the thumbnail S3 key, `Find_and_Show_PDF`'s match,
   * and the vector-store `filename` attribute). Never render one where the
   * other belongs, and never key storage off this field.
   */
  readonly displayName: string;
  /** True when the product store can provide a thumbnail. */
  readonly hasThumbnail: boolean;
  /** Product names/keys that reference this doc, if the store knows them. */
  readonly dependentProjectNames: ReadonlyArray<string>;
  /** Count of product references reported by the product store. */
  readonly usedInProjectCount: number;
  /** How this project reached the document. Retrieval sync may reattach either source under the active workflow id. */
  readonly sources: ReadonlyArray<DocLibrarySource>;
}

export interface DocUploadResult {
  readonly uploaded: number;
  readonly failed: number;
  /** Uploaded to storage but NOT indexed into the vector store. */
  readonly indexFailed: number;
  /** Rejected before upload for an unsupported extension. */
  readonly rejected: ReadonlyArray<string>;
}

/**
 * `IProjectDocsModel` — the product document library for the open project.
 *
 * Documents are associated to a project by the loaded workflow document, not
 * by `docs/index.json` or the project row. Authored `workflow.product` and
 * `knowledge.include[].doc` stay as typed in YAML; lookups go through
 * `normalizeProductNameKey` before Dynamo. Callers should treat "no
 * product/includes" and "no documents for those references" as normal empty
 * results.
 */
export interface IProjectDocsModel {
  /** Emitted whenever the visible item list changes. */
  readonly onChanged: Emitter<ReadonlyArray<DocLibraryItem>>;
  /** Emitted around product-doc fetches and future upload/delete batches. */
  readonly onBusyChanged: Emitter<boolean>;
  /** Free-text progress for a long upload batch (`""` when idle). */
  readonly onProgressChanged: Emitter<string>;

  /** Product-filtered, sorted by `lastModifiedMs` DESC. */
  readonly items: ReadonlyArray<DocLibraryItem>;
  readonly isBusy: boolean;
  readonly progress: string;
  /**
   * Product key used for the last completed document lookup. `null` means no
   * product lookup has completed for the active project yet.
   */
  readonly productKey: string | null;
  /** False while product documents are backed by a read-only browser store. */
  readonly canManageDocuments: boolean;

  /**
   * Fetch product documents. Never throws — a failure yields an empty list
   * and a console warning, matching `AssetLibraryModel.loadAsync`.
   */
  loadAsync(forceReload?: boolean, signal?: AbortSignal): Promise<ReadonlyArray<DocLibraryItem>>;

  /** The documents visible to the open project, sorted by `lastModifiedMs` DESC. */
  getProjectDocsAsync(forceReload?: boolean): Promise<ReadonlyArray<DocLibraryItem> | null>;

  /**
   * Upload is unavailable in this browser store. Throws a clear error so
   * callers cannot keep writing the retired manifest association shape.
   */
  uploadAsync(files: ReadonlyArray<File>, signal?: AbortSignal): Promise<DocUploadResult>;

  /**
   * Delete is unavailable in this browser store. Throws a clear error so
   * callers cannot keep writing the retired manifest association shape.
   */
  deleteAsync(storagePath: string, signal?: AbortSignal): Promise<void>;

  /** Cached `blob:` URL of a PDF's first-page thumbnail, or `null`. */
  getThumbnailUrlAsync(fileName: string): Promise<string | null>;

  /** Open the document in a new browser tab. Resolves false when unavailable. */
  openAsync(storagePath: string): Promise<boolean>;

  /** Drop cached state (sign-out / project switch). Revokes blob URLs. */
  reset(): void;
}

export const PROJECT_DOCS_MODEL = Symbol.for("Campfire.ProjectDocsModel");
